import React, { useState } from 'react';
import Button from '../common/Button';
import StatusMessage from '../common/StatusMessage';
import Loading from '../common/Loading';
import ROITimelineChart from '../charts/ROITimelineChart';
import { convertToCSV, downloadCSV, getCSVColumns } from '../../utils/csvParser';
import { sortAndRenumberProcesses } from '../../utils/scoreCalculator';
import { generateROITable, downloadFile } from '../../services/reportService';

function ROISection({ processes, onProcessChange, contextData, customerId, setProcesses }) {
  const [status, setStatus] = useState(null);
  const [loadingTable, setLoadingTable] = useState(false);
  const costPerAgent = contextData?.cost_per_agent || 25;
  const cases = ['Conservative', 'Likely', 'Optimistic'];

  // Default probability values by case
  const defaultProbabilities = {
    'Conservative': '65%',
    'Likely': '80%',
    'Optimistic': '95%'
  };

  // Default values for ROI fields
  const defaultValues = {
    automation_area: 'Self-service',
    automation_potential: '80%',
    number_of_interactions: '10000',
    avg_time_per_interaction: '30 sec'
  };

  const handleFieldChange = (index, caseType, field, value) => {
    if (onProcessChange) {
      const updatedProcess = {
        ...processes[index],
        roi_cases: {
          ...processes[index].roi_cases,
          [caseType]: {
            ...processes[index].roi_cases?.[caseType],
            [field]: value
          }
        }
      };
      onProcessChange(index, updatedProcess);
    }
  };

  // Handle field changes that should apply to all cases (shared values)
  const handleSharedFieldChange = (index, field, value) => {
    if (onProcessChange) {
      const updatedProcess = {
        ...processes[index],
        roi_cases: {
          Conservative: { ...processes[index].roi_cases?.Conservative, [field]: value },
          Likely: { ...processes[index].roi_cases?.Likely, [field]: value },
          Optimistic: { ...processes[index].roi_cases?.Optimistic, [field]: value }
        }
      };
      onProcessChange(index, updatedProcess);
    }
  };

  // Generate automation potential options (0% to 100% in 10% intervals)
  const automationOptions = [];
  for (let i = 0; i <= 100; i += 10) {
    automationOptions.push(`${i}%`);
  }

  // Generate probability options (50% to 100% in 5% intervals)
  const probabilityOptions = [];
  for (let i = 50; i <= 100; i += 5) {
    probabilityOptions.push(`${i}%`);
  }

  // Automation area options
  const automationAreaOptions = [
    'Agent',
    'Supervisor/Management',
    'Operations',
    'Self-service'
  ];

  // Calculate Automation w/ Probability
  const calculateAutomationWithProbability = (probability, automationPotential) => {
    if (!probability || !automationPotential) return '0%';

    const probValue = parseFloat(probability) / 100;
    const autoValue = parseFloat(automationPotential) / 100;
    const result = probValue * autoValue * 100;

    return `${result.toFixed(1)}%`;
  };

  // Parse time string to seconds
  const parseTimeToSeconds = (timeStr) => {
    if (!timeStr) return 0;

    const numMatch = timeStr.match(/[\d.]+/);
    if (!numMatch) return 0;

    const value = parseFloat(numMatch[0]);
    const lowerStr = timeStr.toLowerCase();

    if (lowerStr.includes('hour') || lowerStr.includes('hr') || lowerStr.includes('h')) {
      return value * 3600;
    } else if (lowerStr.includes('min') || lowerStr.includes('m')) {
      return value * 60;
    } else if (lowerStr.includes('sec') || lowerStr.includes('s')) {
      return value;
    }

    // Default to seconds if no unit specified
    return value;
  };

  // Calculate Total Interactions (per month)
  const calculateTotalInteractions = (numberOfInteractions, automationWProbability) => {
    if (!numberOfInteractions || !automationWProbability) return '0';

    const interactions = parseFloat(numberOfInteractions);
    const percentage = parseFloat(automationWProbability) / 100;
    const result = interactions * percentage;

    return result.toFixed(0);
  };

  // Calculate Total Time Saved (per month) in seconds
  const calculateTotalTimeSaved = (totalInteractions, avgTimePerInteraction) => {
    if (!totalInteractions || !avgTimePerInteraction) return '0';

    const interactions = parseFloat(totalInteractions);
    const timeInSeconds = parseTimeToSeconds(avgTimePerInteraction);
    const result = interactions * timeInSeconds;

    return result.toFixed(0);
  };

  // Calculate Savings per Month
  const calculateSavingsPerPeriod = (totalTimeSaved, costPerAgent) => {
    if (!totalTimeSaved || !costPerAgent) return '$0.00';

    const timeInHours = parseFloat(totalTimeSaved) / 3600;
    const savings = timeInHours * costPerAgent;

    return `$${savings.toFixed(2)}`;
  };

  const handleSaveProcesses = () => {
    if (processes.length === 0) {
      setStatus({ type: 'error', message: 'No processes to save' });
      return;
    }

    // Sort and renumber before saving
    const sortedProcesses = sortAndRenumberProcesses(processes);
    setProcesses(sortedProcesses);

    // Save to localStorage (use customerId if available, otherwise use 'default')
    const storageKey = customerId ? `processes_${customerId}` : 'processes_default';
    localStorage.setItem(storageKey, JSON.stringify(sortedProcesses));

    setStatus({
      type: 'success',
      message: `✅ Saved ${sortedProcesses.length} processes! Ready to generate report.`
    });
  };

  const handleExportCSV = () => {
    if (processes.length === 0) {
      setStatus({ type: 'error', message: 'No processes to export' });
      return;
    }

    // Sort and renumber before export
    const sortedProcesses = sortAndRenumberProcesses(processes);

    const columns = getCSVColumns('final');
    const csvContent = convertToCSV(sortedProcesses, columns);
    downloadCSV(csvContent, 'proc_list_export.csv');

    setStatus({
      type: 'success',
      message: `✅ Exported ${sortedProcesses.length} processes to CSV!`
    });
  };

  const handleGenerateROITable = async () => {
    if (!customerId || !contextData) {
      setStatus({ type: 'error', message: 'Please save context first' });
      return;
    }

    if (processes.length === 0) {
      setStatus({ type: 'error', message: 'No processes to generate table from' });
      return;
    }

    setLoadingTable(true);
    setStatus(null);

    try {
      console.log('Generating standalone ROI table with merged cells...');

      // Generate table with docx library (has merged cells support)
      const result = await generateROITable(customerId, contextData, processes);

      console.log('ROI table generated successfully!');

      // Convert base64 to blob
      const byteCharacters = atob(result.tableBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const tableBlob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });

      const tableUrl = URL.createObjectURL(tableBlob);

      // Download immediately
      downloadFile(tableUrl, 'ROI_Analysis_Table.docx');

      setStatus({
        type: 'success',
        message: '✅ ROI table with merged cells downloaded successfully!'
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: `Failed to generate ROI table: ${error.response?.data?.error || error.message}`
      });
    } finally {
      setLoadingTable(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">ROI Analysis</h2>
      <p className="text-gray-600 text-sm mb-6">Review and analyze the return on investment for each assessed process.</p>

      {/* ROI Configuration Fields */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">ROI Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cost per Agent ($)
            </label>
            <input
              type="number"
              value={contextData?.cost_per_agent || 25}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
              title="Set this value in the Customer Context tab"
            />
            <p className="text-xs text-gray-500 mt-1">Set in Customer Context tab</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Professional Services Price ($)
            </label>
            <input
              type="number"
              value={contextData?.professional_services_price || 0}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
              title="Set this value in the Customer Context tab"
            />
            <p className="text-xs text-gray-500 mt-1">Set in Customer Context tab</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bill of Materials Price ($)
            </label>
            <input
              type="number"
              value={contextData?.bom_price || 0}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
              title="Set this value in the Customer Context tab"
            />
            <p className="text-xs text-gray-500 mt-1">Set in Customer Context tab</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bill of Materials Length (months)
            </label>
            <input
              type="number"
              value={contextData?.bom_length_months || 0}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
              title="Set this value in the Customer Context tab"
            />
            <p className="text-xs text-gray-500 mt-1">Set in Customer Context tab</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estimated Monthly Usage Costs ($)
            </label>
            <input
              type="number"
              value={contextData?.monthly_usage_costs || 0}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
              title="Set this value in the Customer Context tab"
            />
            <p className="text-xs text-gray-500 mt-1">Set in Customer Context tab</p>
          </div>
        </div>
      </div>

      {status && (
        <StatusMessage
          type={status.type}
          message={status.message}
          onClose={() => setStatus(null)}
        />
      )}

      {loadingTable && <Loading message="Generating ROI table with merged cells..." />}

      {processes.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-600">No processes available. Please add processes in the Process Assessment tab first.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse border border-gray-300">
            <thead className="bg-gray-200">
              <tr>
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold">Process ID</th>
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold">Process Name</th>
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold">Automation Area</th>
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold">Automation Potential</th>
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold">Case</th>
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold">Probability</th>
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold">Automation w/ Probability</th>
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold">Number of Interactions (per month)</th>
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold">Total Interactions (per month)</th>
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold">Avg Time (Per Interaction)</th>
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold">Total time saved (per month)</th>
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold">Savings per Month</th>
              </tr>
            </thead>
            <tbody>
              {processes.map((process, processIndex) => (
                <React.Fragment key={process.id || processIndex}>
                  {cases.map((caseType, caseIndex) => {
                    const caseData = process.roi_cases?.[caseType] || {};
                    const isFirstRow = caseIndex === 0;

                    // Calculate derived values
                    const automationWProbability = calculateAutomationWithProbability(
                      caseData.probability || defaultProbabilities[caseType],
                      caseData.automation_potential || defaultValues.automation_potential
                    );
                    const totalInteractions = calculateTotalInteractions(
                      caseData.number_of_interactions || defaultValues.number_of_interactions,
                      automationWProbability
                    );
                    const totalTimeSaved = calculateTotalTimeSaved(
                      totalInteractions,
                      caseData.avg_time_per_interaction || defaultValues.avg_time_per_interaction
                    );
                    const savingsPerPeriod = calculateSavingsPerPeriod(
                      totalTimeSaved,
                      costPerAgent
                    );

                    return (
                      <tr key={`${process.id}-${caseType}`} className="hover:bg-gray-50">
                        {isFirstRow && (
                          <>
                            <td className="border border-gray-300 px-4 py-2 text-sm font-medium" rowSpan={3}>
                              {process.id}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-sm" rowSpan={3}>
                              {process.name}
                            </td>
                            <td className="border border-gray-300 px-4 py-2" rowSpan={3}>
                              <select
                                value={caseData.automation_area || defaultValues.automation_area}
                                onChange={(e) => handleSharedFieldChange(processIndex, 'automation_area', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              >
                                <option value="">Select...</option>
                                {automationAreaOptions.map(option => (
                                  <option key={option} value={option}>{option}</option>
                                ))}
                              </select>
                            </td>
                            <td className="border border-gray-300 px-4 py-2" rowSpan={3}>
                              <select
                                value={caseData.automation_potential || defaultValues.automation_potential}
                                onChange={(e) => handleSharedFieldChange(processIndex, 'automation_potential', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              >
                                {automationOptions.map(option => (
                                  <option key={option} value={option}>{option}</option>
                                ))}
                              </select>
                            </td>
                          </>
                        )}
                        <td className="border border-gray-300 px-4 py-2 text-sm font-medium bg-gray-50">
                          {caseType}
                        </td>
                        <td className="border border-gray-300 px-4 py-2">
                          <select
                            value={caseData.probability || defaultProbabilities[caseType]}
                            onChange={(e) => handleFieldChange(processIndex, caseType, 'probability', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            {probabilityOptions.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-sm text-center bg-blue-50 font-medium">
                          {automationWProbability}
                        </td>
                        {isFirstRow && (
                          <>
                            <td className="border border-gray-300 px-4 py-2" rowSpan={3}>
                              <input
                                type="text"
                                value={caseData.number_of_interactions !== undefined ? caseData.number_of_interactions : defaultValues.number_of_interactions}
                                onChange={(e) => handleSharedFieldChange(processIndex, 'number_of_interactions', e.target.value)}
                                placeholder="e.g., 1000"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </td>
                          </>
                        )}
                        <td className="border border-gray-300 px-4 py-2 text-sm text-center bg-green-50 font-medium">
                          {totalInteractions}
                        </td>
                        {isFirstRow && (
                          <>
                            <td className="border border-gray-300 px-4 py-2" rowSpan={3}>
                              <input
                                type="text"
                                value={caseData.avg_time_per_interaction !== undefined ? caseData.avg_time_per_interaction : defaultValues.avg_time_per_interaction}
                                onChange={(e) => handleSharedFieldChange(processIndex, 'avg_time_per_interaction', e.target.value)}
                                placeholder="e.g., 5 min"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </td>
                          </>
                        )}
                        <td className="border border-gray-300 px-4 py-2 text-sm text-center bg-green-50 font-medium">
                          {totalTimeSaved} sec
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-sm text-center bg-yellow-50 font-medium">
                          {savingsPerPeriod}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {processes.length > 0 && (
        <>
          <div className="mt-6 overflow-x-auto">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Total Savings Summary</h3>
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-200">
                <tr>
                  <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold">Case</th>
                  <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold">Total Savings per Month</th>
                </tr>
              </thead>
              <tbody>
                {cases.map((caseType) => {
                  // Calculate total savings for this case across all processes
                  const totalSavings = processes.reduce((sum, process) => {
                    const caseData = process.roi_cases?.[caseType] || {};

                    const automationWProbability = calculateAutomationWithProbability(
                      caseData.probability || defaultProbabilities[caseType],
                      caseData.automation_potential || defaultValues.automation_potential
                    );
                    const totalInteractions = calculateTotalInteractions(
                      caseData.number_of_interactions || defaultValues.number_of_interactions,
                      automationWProbability
                    );
                    const totalTimeSaved = calculateTotalTimeSaved(
                      totalInteractions,
                      caseData.avg_time_per_interaction || defaultValues.avg_time_per_interaction
                    );

                    const timeInHours = parseFloat(totalTimeSaved) / 3600;
                    const savings = timeInHours * costPerAgent;

                    return sum + savings;
                  }, 0);

                  return (
                    <tr key={caseType} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2 text-sm font-medium bg-gray-50">
                        {caseType}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-sm text-center bg-yellow-50 font-semibold">
                        ${totalSavings.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ROI Analysis Summary */}
          <div className="mt-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
            <h3 className="text-xl font-bold text-gray-800 mb-4">ROI Analysis Summary</h3>

            {(() => {
              // Calculate costs
              const professionalServices = contextData?.professional_services_price || 0;
              const bomPrice = contextData?.bom_price || 0;
              const bomLengthMonths = contextData?.bom_length_months || 1;
              const monthlyUsageCosts = contextData?.monthly_usage_costs || 0;

              const monthlyBOMCost = bomLengthMonths > 0 ? bomPrice / bomLengthMonths : 0;
              const totalMonthlyCosts = monthlyBOMCost + monthlyUsageCosts;

              // Calculate total savings for each case
              const savingsByCase = {};
              cases.forEach((caseType) => {
                savingsByCase[caseType] = processes.reduce((sum, process) => {
                  const caseData = process.roi_cases?.[caseType] || {};

                  const automationWProbability = calculateAutomationWithProbability(
                    caseData.probability || defaultProbabilities[caseType],
                    caseData.automation_potential || defaultValues.automation_potential
                  );
                  const totalInteractions = calculateTotalInteractions(
                    caseData.number_of_interactions || defaultValues.number_of_interactions,
                    automationWProbability
                  );
                  const totalTimeSaved = calculateTotalTimeSaved(
                    totalInteractions,
                    caseData.avg_time_per_interaction || defaultValues.avg_time_per_interaction
                  );

                  const timeInHours = parseFloat(totalTimeSaved) / 3600;
                  const savings = timeInHours * costPerAgent;

                  return sum + savings;
                }, 0);
              });

              return (
                <>
                  {/* Cost Breakdown */}
                  <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white p-4 rounded-lg shadow">
                      <h4 className="font-semibold text-gray-700 mb-3">Cost Breakdown</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Professional Services (one-time):</span>
                          <span className="font-semibold">${professionalServices.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Monthly BOM Cost:</span>
                          <span className="font-semibold">${monthlyBOMCost.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Monthly Usage Costs:</span>
                          <span className="font-semibold">${monthlyUsageCosts.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-gray-200">
                          <span className="text-gray-800 font-semibold">Total Monthly Costs:</span>
                          <span className="font-bold text-blue-600">${totalMonthlyCosts.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-lg shadow">
                      <h4 className="font-semibold text-gray-700 mb-3">Cost Details</h4>
                      <div className="space-y-2 text-sm text-gray-600">
                        <div>BOM Price: ${bomPrice.toFixed(2)}</div>
                        <div>BOM Length: {bomLengthMonths} months</div>
                        <div className="pt-2 border-t border-gray-200 text-xs text-gray-500">
                          Monthly BOM Cost = BOM Price ÷ BOM Length
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ROI Timeline by Case */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse border border-gray-300 bg-white">
                      <thead className="bg-indigo-100">
                        <tr>
                          <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold">Case</th>
                          <th className="border border-gray-300 px-4 py-3 text-center text-sm font-semibold">Monthly Savings</th>
                          <th className="border border-gray-300 px-4 py-3 text-center text-sm font-semibold">Monthly Costs</th>
                          <th className="border border-gray-300 px-4 py-3 text-center text-sm font-semibold">Net Monthly Savings</th>
                          <th className="border border-gray-300 px-4 py-3 text-center text-sm font-semibold">Months to Payoff</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cases.map((caseType) => {
                          const monthlySavings = savingsByCase[caseType];
                          const netMonthlySavings = monthlySavings - totalMonthlyCosts;
                          const monthsToPayoff = netMonthlySavings > 0
                            ? professionalServices / netMonthlySavings
                            : Infinity;

                          const bgColor = caseType === 'Conservative' ? 'bg-red-50' :
                                         caseType === 'Likely' ? 'bg-yellow-50' : 'bg-green-50';

                          return (
                            <tr key={caseType} className={`hover:bg-gray-50 ${bgColor}`}>
                              <td className="border border-gray-300 px-4 py-2 text-sm font-medium">
                                {caseType}
                              </td>
                              <td className="border border-gray-300 px-4 py-2 text-sm text-center">
                                ${monthlySavings.toFixed(2)}
                              </td>
                              <td className="border border-gray-300 px-4 py-2 text-sm text-center">
                                ${totalMonthlyCosts.toFixed(2)}
                              </td>
                              <td className={`border border-gray-300 px-4 py-2 text-sm text-center font-semibold ${
                                netMonthlySavings > 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                ${netMonthlySavings.toFixed(2)}
                              </td>
                              <td className="border border-gray-300 px-4 py-2 text-sm text-center font-bold">
                                {netMonthlySavings > 0 ? (
                                  monthsToPayoff < 100 ? (
                                    <span className="text-indigo-600">{monthsToPayoff.toFixed(1)} months</span>
                                  ) : (
                                    <span className="text-gray-500">{monthsToPayoff.toFixed(0)}+ months</span>
                                  )
                                ) : (
                                  <span className="text-red-600">No payoff</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 p-3 bg-blue-100 rounded text-sm text-gray-700">
                    <strong>Note:</strong> Months to Payoff = Professional Services Cost ÷ Net Monthly Savings
                    <br />
                    Net Monthly Savings = Monthly Savings - (Monthly BOM Cost + Monthly Usage Costs)
                  </div>
                </>
              );
            })()}
          </div>

          {/* ROI Timeline Chart */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">ROI Timeline Visualization</h3>
            <ROITimelineChart processes={processes} contextData={contextData} />
          </div>
        </>
      )}

      {processes.length > 0 && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button
            variant="success"
            onClick={handleSaveProcesses}
            disabled={processes.length === 0}
          >
            Save Process List
          </Button>

          <Button
            variant="secondary"
            onClick={handleExportCSV}
            disabled={processes.length === 0}
          >
            Export as CSV
          </Button>

          <Button
            variant="primary"
            onClick={handleGenerateROITable}
            disabled={processes.length === 0 || loadingTable || !customerId}
          >
            {loadingTable ? 'Generating...' : 'Download ROI Table (Merged Cells)'}
          </Button>
        </div>
      )}
    </div>
  );
}

export default ROISection;
